import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, Info, X, XCircle } from 'lucide-react'
import { Button } from '../../../../components'
import { formatDateDisplay } from '../constants/hrmsLeaveRequests.constants'
import type { StaffLeaveRequest } from '../types/hrmsLeaveRequests.types'

interface RejectLeaveModalProps {
  isOpen: boolean
  request: StaffLeaveRequest | null
  onClose: () => void
  onConfirmReject: (requestId: string, reason: string) => void
  isSubmitting?: boolean
}

function getInitials(name: string): string {
  const parts = name.trim().split(' ')
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

export function RejectLeaveModal({
  isOpen,
  request,
  onClose,
  onConfirmReject,
  isSubmitting = false,
}: RejectLeaveModalProps) {
  const [rejectionReason, setRejectionReason] = useState<string>('')
  const [reasonTouched, setReasonTouched] = useState<boolean>(false)

  useEffect(() => {
    if (!isOpen) {
      setRejectionReason('')
      setReasonTouched(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        e.stopPropagation()
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [isOpen, isSubmitting, onClose])

  if (!isOpen || !request) return null

  const handleConfirm = () => {
    setReasonTouched(true)
    const trimmed = rejectionReason.trim()
    if (!trimmed || isSubmitting) return
    onConfirmReject(request.id, trimmed)
  }

  const leaveDatesFormatted = `${formatDateDisplay(request.from)} – ${formatDateDisplay(request.to)}`

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reject-leave-modal-title"
      className="fixed inset-0 z-[150] flex items-center justify-center p-4 overflow-x-hidden font-sans"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={isSubmitting ? undefined : onClose}
      />

      {/* Modal Box */}
      <div className="relative z-10 w-full max-w-[520px] max-h-[90vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 p-5 flex items-start justify-between shrink-0 gap-3">
          <div className="flex items-start gap-2.5 min-w-0">
            <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h2 id="reject-leave-modal-title" className="text-lg font-bold text-slate-900 truncate">
                Reject Leave Request
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Please provide a reason for rejecting this leave request.
              </p>
            </div>
          </div>

          <button
            type="button"
            aria-label="Close dialog"
            disabled={isSubmitting}
            onClick={onClose}
            className="rounded-full p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition cursor-pointer shrink-0 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5 overflow-y-auto flex-1">
          {/* Employee Card */}
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 flex items-center justify-between min-w-0 gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-full bg-[#146971] text-white font-bold flex items-center justify-center shrink-0 text-sm">
                {getInitials(request.name)}
              </div>
              <div className="min-w-0">
                <p className="font-bold text-slate-900 truncate text-sm">{request.name}</p>
                <p className="text-xs text-slate-500 truncate mt-0.5">
                  <span className="font-semibold text-[#146971]">{request.employeeId}</span> • {leaveDatesFormatted}
                </p>
              </div>
            </div>

            <div className="shrink-0">
              <span className="bg-amber-50 text-amber-800 border border-amber-200 text-xs font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                Pending Approval
              </span>
            </div>
          </div>

          {/* Textarea Section */}
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-1">
              Reason for Rejection <span className="text-red-500">*</span>
            </label>
            <textarea
              value={rejectionReason}
              disabled={isSubmitting}
              onChange={(e) => {
                setRejectionReason(e.target.value)
                setReasonTouched(true)
              }}
              placeholder="Enter reason for rejecting this leave request..."
              rows={4}
              className={`min-h-[100px] w-full rounded-xl bg-gray-100 border p-3 text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#146971] focus:border-transparent resize-none ${
                reasonTouched && !rejectionReason.trim()
                  ? 'border-red-400 bg-red-50/50'
                  : 'border-gray-200'
              }`}
            />
            {reasonTouched && !rejectionReason.trim() && (
              <p className="text-xs text-red-600 font-medium mt-1">
                Rejection reason is required.
              </p>
            )}
          </div>

          {/* Info Note */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex items-start gap-2.5 text-xs text-slate-600">
            <Info className="h-4 w-4 text-slate-500 shrink-0 mt-0.5" />
            <p>
              The rejection reason will be recorded with the leave request and will be visible to the employee.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 shrink-0">
          <Button
            variant="secondary"
            size="md"
            disabled={isSubmitting}
            onClick={onClose}
            className="border-none bg-transparent hover:bg-gray-200 text-slate-600 font-semibold"
          >
            Cancel
          </Button>

          <button
            type="button"
            disabled={!rejectionReason.trim() || isSubmitting}
            onClick={handleConfirm}
            className="bg-red-700 hover:bg-red-800 disabled:opacity-50 text-white font-semibold rounded-lg px-5 py-2.5 text-sm inline-flex items-center gap-2 transition cursor-pointer"
          >
            <XCircle className="h-4 w-4" />
            {isSubmitting ? 'Rejecting...' : 'Confirm Rejection'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
